/* eslint max-lines: ["error", { max: 285 }] -- v2 group/section view-model mappers + labels */
import compact from "lodash/compact";
import initial from "lodash/initial";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import last from "lodash/last";
import toNumber from "lodash/toNumber";

import { DateFormat } from "@/constants/DateFormat";
import { GroupSectionFrequencyCode } from "@/constants/Schedule";
import type { GroupClassCardItem } from "@/ui/components/groupClasses/series/GroupClassCard";
import type { AvatarCardProps } from "@/ui/core/AvatarCard";
import { formatISODate, getEndTime, getTimeRange } from "@/utils/date.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

/**
 * Builds the "Hosted by …" label for a group class from its hosts, appending each
 * host's credential (subtitle) when present, e.g. "Hosted by John Mendoza, MD and
 * Christine Planchine, RDN". Returns undefined when there are no hosts so callers
 * can hide the row.
 */
const getHostsLabel = (hosts?: AvatarCardProps[]): string | undefined => {
  if (isNil(hosts) || isEmpty(hosts)) return undefined;

  const names = hosts.map((host) =>
    isNotNilNotEmpty(host.subtitle)
      ? `${host.userName}, ${host.subtitle}`
      : host.userName,
  );

  const lastName = last(names);
  const leadingNames = initial(names);
  return isEmpty(leadingNames)
    ? `Hosted by ${lastName}`
    : `Hosted by ${leadingNames.join(", ")} and ${lastName}`;
};

// group_section_frequency codes → display label (schedule-service reference table)
const GroupSectionFrequencyLabels: Record<GroupSectionFrequencyCode, string> = {
  [GroupSectionFrequencyCode.DoesNotRepeat]: "Does not repeat",
  [GroupSectionFrequencyCode.Every4Weeks]: "Every 4 weeks",
  [GroupSectionFrequencyCode.EveryDay]: "Every day",
  [GroupSectionFrequencyCode.EveryOtherWeek]: "Every other week",
  [GroupSectionFrequencyCode.EveryWeekday]: "Every weekday (M-F)",
  [GroupSectionFrequencyCode.FirstOfEveryMonth]: "First of every month",
  [GroupSectionFrequencyCode.Weekly]: "Weekly",
};

// weekly-cadence frequencies render as "<prefix> <weekday>", e.g. "Every Monday",
// "Every other Wednesday", "Every 4th Thursday".
const SectionWeekdayRecurrencePrefixes: Partial<
  Record<GroupSectionFrequencyCode, string>
> = {
  [GroupSectionFrequencyCode.Every4Weeks]: "Every 4th",
  [GroupSectionFrequencyCode.EveryOtherWeek]: "Every other",
  [GroupSectionFrequencyCode.Weekly]: "Every",
};

// narrows an arbitrary API frequency string to a known GroupSectionFrequencyCode
const isGroupSectionFrequencyCode = (
  code?: string,
): code is GroupSectionFrequencyCode =>
  isNotNilNotEmpty(code) && code in GroupSectionFrequencyLabels;

// builds a section's recurrence title matching the group-details design:
//   weekly cadences → "<prefix> <weekday>" (e.g. "Every Monday")
//   does-not-repeat → the event date itself (e.g. "Friday, April 16")
//   other cadences  → the plain frequency label (e.g. "Every day")
const getSectionRecurrenceTitle = (section: GroupSection): string => {
  const { frequency, startDate } = section;
  if (!isGroupSectionFrequencyCode(frequency)) return "";

  const weekdayPrefix = SectionWeekdayRecurrencePrefixes[frequency];

  if (weekdayPrefix && isNotNilNotEmpty(startDate)) {
    const weekday = formatISODate({
      dateString: startDate,
      format: DateFormat.weekDay,
    });
    return `${weekdayPrefix} ${weekday}`;
  }

  if (
    frequency === GroupSectionFrequencyCode.DoesNotRepeat &&
    isNotNilNotEmpty(startDate)
  ) {
    return formatISODate({
      dateString: startDate,
      format: DateFormat.weekDayMonthDay,
    });
  }

  return GroupSectionFrequencyLabels[frequency];
};

// the frequency label shown beneath the date, e.g. "Weekly", "Every day",
// "Does not repeat". Undefined for an unknown frequency code.
const getRegisteredGroupLabel = (group: RegisteredGroup): string | undefined =>
  isGroupSectionFrequencyCode(group.frequencyCode)
    ? GroupSectionFrequencyLabels[group.frequencyCode]
    : undefined;

// derives a "start - end" time range (e.g. "4 - 4:30pm") from a start time + duration,
// or "" when either is missing. Shared by the registered-group and session view-models.
const getSectionTimeRange = ({
  durationMinutes,
  startDate,
}: {
  durationMinutes?: number;
  startDate?: string;
}): string =>
  isNotNilNotEmpty(startDate) && !isNil(durationMinutes)
    ? getTimeRange({
        endDate:
          getEndTime({ durationMinutes, startDate })?.toString() ?? undefined,
        shouldIncludeStartAmPm: true,
        startDate,
      })
    : "";

const getRegisteredGroupRecurrence = (group: RegisteredGroup): string => {
  const frequencyLabel = getRegisteredGroupLabel(group);

  const timeRange = getSectionTimeRange({
    durationMinutes: group.duration,
    startDate: group.nextEventStartTime,
  });

  return compact([frequencyLabel, timeRange]).join(", ");
};

// maps a v2 available group → GroupClassCard view-model (shows "N available options").
// The groups-list DTO has no per-section schedule fields, so registered date/recurrence
// are not shown here — the section schedule lives on the group details response.
const toAvailableGroupCardItem = (
  group: AvailableGroup,
): GroupClassCardItem => ({
  description: group.description,
  iconName: group.iconName,
  id: group.groupId,
  isCore: group.isCore,
  isRegistered: group.isUserRegistered,
  numberOfOptions: group.numberOfSections,
  title: group.title,
});

// maps a v2 recommended group → GroupClassCard view-model (shows "N available options")
const toRecommendedGroupCardItem = (
  group: RecommendedGroup,
): GroupClassCardItem => ({
  description: group.description,
  iconName: group.iconName,
  id: group.groupId,
  isCore: group.isCore,
  numberOfOptions: group.instances.length,
  title: group.title,
});

// the bold date line for a registered group. Repeating groups read "Next on <date>";
// does-not-repeat groups show the single event date on its own (no "Next on" prefix).
const getRegisteredGroupDateString = (
  group: RegisteredGroup,
): string | undefined => {
  const dateString = group.nextEventStartTime ?? group.startDate;
  if (!isNotNilNotEmpty(dateString)) return undefined;
  const formattedDate = formatISODate({
    dateString,
    format: DateFormat.weekDayShortMonthDay,
  });
  return group.frequencyCode === GroupSectionFrequencyCode.DoesNotRepeat
    ? formattedDate
    : `Next on ${formattedDate}`;
};

// maps a v2 registered group → GroupClassCard view-model (shows next date + recurrence)
// registered cards omit the description — they lead with the next-date/recurrence footer
const toRegisteredGroupCardItem = (
  group: RegisteredGroup,
): GroupClassCardItem => ({
  // a user can be registered in multiple sections of the same group; carry the section id
  // so lists can key uniquely (id/groupId repeats across sections) and navigate by section
  groupSectionId: group.groupSectionId,
  iconName: group.iconName,
  // navigate by groupId: the details screen queries by group
  id: group.groupId,
  isCore: group.isCore,
  isRegistered: true,
  nextDateString: getRegisteredGroupDateString(group),
  recurrenceString: getRegisteredGroupRecurrence(group),
  title: group.title,
});

// group_section_session codes encode the count as the trailing number, e.g. SESSION_16 → 16
const getSessionCountFromCode = (sessionCode?: string): number => {
  if (!isNotNilNotEmpty(sessionCode)) return 0;
  const count = toNumber(last(sessionCode.split("_")));
  return isNaN(count) ? 0 : count;
};

interface GroupSessionCardItem {
  hosts?: AvatarCardProps[];
  id: string;
  // an open-ended series: no session count, no end date, and a recurring frequency
  // (not "does not repeat"). The card shows an "Ongoing" tag instead of a session count
  isOngoing: boolean;
  isUserRegistered: boolean;
  nextDateString?: string;
  sessionCount: number;
  timeRange: string;
  title: string;
}

// maps a v2 group section → session card view-model:
// title "Every Monday", timeRange "4 - 4:30pm", sessionCount from the session code
const toGroupSession = (section: GroupSection): GroupSessionCardItem => {
  const title = getSectionRecurrenceTitle(section);

  // prefer the actual next occurrence from the events table; fall back to the section
  // start date only when the API omits it (mirrors getRegisteredGroupDateString)
  const nextDate = section.nextEventStartTime ?? section.startDate;

  // derive the time range from the same next-occurrence date the carousel uses, so the
  // details screen and the "Your Groups" card show the same time for a section
  const timeRange = getSectionTimeRange({
    durationMinutes: section.duration,
    startDate: nextDate,
  });
  const nextDateString = isNotNilNotEmpty(nextDate)
    ? `Next on ${formatISODate({
        dateString: nextDate,
        format: DateFormat.weekDayMonthDay,
      })}`
    : undefined;

  const sessionCount = getSessionCountFromCode(section.sessionCode);

  const isOngoing =
    sessionCount === 0 &&
    !isNotNilNotEmpty(section.endDate) &&
    section.frequency !== GroupSectionFrequencyCode.DoesNotRepeat;

  return {
    hosts: section.hosts,
    id: section.groupSectionId,
    isOngoing,
    isUserRegistered: section.isUserRegistered,
    nextDateString,
    sessionCount,
    timeRange,
    title,
  };
};

const toGroupSessions = (group: GroupDetails): GroupSessionCardItem[] =>
  group.sections.map(toGroupSession);

// a sensitive group's registration is private to the patient and their provider team, so a
// user viewing someone else's groups (only a support can — a patient's selected user is
// always themselves) must not see them. Returns true when the selected user is not the
// logged-in app user.
const shouldHideSensitiveGroups = ({
  appUserId,
  selectedUserId,
}: {
  appUserId: string;
  selectedUserId: string;
}): boolean => selectedUserId !== appUserId;

// drops sensitive groups from a list; used wherever a user is viewing another user's groups
const excludeSensitiveGroups = <T extends { isSensitive?: boolean }>(
  groups: T[],
): T[] => groups.filter((group) => !group.isSensitive);

export {
  excludeSensitiveGroups,
  getHostsLabel,
  shouldHideSensitiveGroups,
  toAvailableGroupCardItem,
  toGroupSessions,
  toRecommendedGroupCardItem,
  toRegisteredGroupCardItem,
};
export type { GroupSessionCardItem };
